import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Briefcase, Info, AlertTriangle, CheckCircle } from 'lucide-react'
import { supabaseClient } from '../config/supabase'
import { useAuthStore } from '../store/authStore'
import type { Notification } from '../types'

const typeIcons: Record<string, React.ElementType> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertTriangle,
  job_match: Briefcase,
  survey: Info,
  system: Info,
}

const typeColors: Record<string, string> = {
  info: 'text-blue-500 bg-blue-50',
  success: 'text-green-500 bg-green-50',
  warning: 'text-amber-500 bg-amber-50',
  error: 'text-red-500 bg-red-50',
  job_match: 'text-purple-500 bg-purple-50',
  survey: 'text-indigo-500 bg-indigo-50',
  system: 'text-gray-500 bg-gray-50',
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString()
}

const NotificationBell: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const unreadCount = notifications.filter(n => !n.read).length

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return
    setLoading(true)
    try {
      const { data, error } = await supabaseClient
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20)

      if (!error && data) {
        setNotifications(data as Notification[])
      }
    } catch (err) {
      console.warn('Failed to fetch notifications:', err)
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    fetchNotifications()
    // Poll every 30 seconds
    const interval = setInterval(fetchNotifications, 30000)
    return () => clearInterval(interval)
  }, [fetchNotifications])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const markAsRead = async (id: string) => {
    const { error } = await supabaseClient
      .from('notifications')
      .update({ read: true })
      .eq('id', id)
      .eq('user_id', user!.id)

    if (!error) {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
    }
  }

  const markAllRead = async () => {
    if (!user?.id) return
    const { error } = await supabaseClient
      .from('notifications')
      .update({ read: true })
      .eq('user_id', user.id)
      .eq('read', false)

    if (!error) {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    }
  }

  const deleteAllRead = async () => {
    if (!user?.id) return
    const { error } = await supabaseClient
      .from('notifications')
      .delete()
      .eq('user_id', user.id)
      .eq('read', true)

    if (!error) {
      setNotifications(prev => prev.filter(n => !n.read))
    }
  }

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id)
    setOpen(false)

    const t = notification.title
    // Alumni routes
    if (t === 'Job Invitation' || t === 'Job Invitation No Longer Available' ||
        t === 'Application Update' || t === 'Application Cancelled — Job Listing Closed') {
      navigate('/my-applications')
    } else if (notification.type === 'survey' || t === 'New Survey Available' || t === 'Survey Updated') {
      navigate('/survey')
    } else if (t === 'Feedback Requested') {
      navigate('/provide-feedback')
    } else if (t === 'Feedback Request Accepted' || t === 'Feedback Request Declined' || t === 'Feedback Received') {
      navigate('/employer/feedback')
    } else if (t === 'Registration Approved' || t === 'Registration Update') {
      navigate('/profile')
    // Employer routes
    } else if (t === 'New Job Application') {
      navigate('/employer/applications')
    } else if (t === 'Job Listing Closed by UCLM Admin') {
      navigate('/employer/jobs')
    } else if (t === 'Partnership Approved' || t === 'Partnership Update') {
      navigate('/employer/jobs')
    }
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center
            bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-3">
              {notifications.some(n => n.read) && (
                <button
                  onClick={deleteAllRead}
                  className="text-xs text-red-500 hover:text-red-700 font-medium"
                >
                  Delete read
                </button>
              )}
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No notifications yet</p>
              </div>
            ) : (
              notifications.map(n => {
                const Icon = typeIcons[n.type] || Info
                const color = typeColors[n.type] || typeColors.info
                return (
                  <button
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition flex gap-3
                      ${!n.read ? 'bg-blue-50/30' : ''}`}
                  >
                    <div className={`p-1.5 rounded-lg flex-shrink-0 ${color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${!n.read ? 'font-semibold text-gray-900' : 'text-gray-700'} line-clamp-2`}>
                        {n.title}
                      </p>
                      <p className="text-xs text-gray-500 line-clamp-3 mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />
                    )}
                  </button>
                )
              })
            )}
          </div>

          {/* Footer — View all */}
          <div className="border-t border-gray-100">
            <button
              onClick={() => { setOpen(false); navigate('/notifications') }}
              className="w-full py-2.5 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors text-center"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell
